//Frontend/src/unicodex_ui/pages/PosInventory.jsx
import React, { useEffect, useMemo, useState } from "react";
import ManufacturerNav from "../components/ManufacturerNav";
import portalPosApi from "../services/portalPosApi";
import {
  createPosProductSubmission,
  listMyPosProductSubmissions,
} from "../services/posInventoryApi";
import "../styles/PortalBusiness.css";

function money(n) {
  const x = Number(n);
  if (!Number.isFinite(x)) return "-";
  return x.toFixed(2);
}

export default function PosInventory() {
  const [branches, setBranches] = useState([]);
  const [branchId, setBranchId] = useState("");
  const [name, setName] = useState("");
  const [unitPrice, setUnitPrice] = useState("");
  const [taxRate, setTaxRate] = useState("");
  const [initialQuantity, setInitialQuantity] = useState("");

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const canSubmit = useMemo(() => {
    return (
      String(branchId).trim() &&
      String(name).trim().length >= 2 &&
      String(unitPrice).trim() !== "" &&
      Number(unitPrice) >= 0 &&
      // taxRate is optional; if provided must be within [0..1]
      (String(taxRate).trim() === "" ||
        (Number(taxRate) >= 0 && Number(taxRate) <= 1)) &&
      String(initialQuantity).trim() !== "" &&
      Number(initialQuantity) > 0
    );
  }, [branchId, name, unitPrice, taxRate, initialQuantity]);

  async function load() {
    setErr("");
    setLoading(true);
    try {
      // 1) branches
      const b = await portalPosApi.listBranches();
      setBranches(Array.isArray(b?.branches) ? b.branches : []);

      // 2) submissions
      const res = await listMyPosProductSubmissions({ mine: 1, limit: 50, offset: 0 });
      setRows(Array.isArray(res?.rows) ? res.rows : []);
    } catch (e) {
      const msg =
        e?.response?.data?.error
          ? `Load failed: ${e.response.data.error}`
          : "Unable to load POS inventory submissions.";
      setErr(msg);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function onSubmit(e) {
    e.preventDefault();
    if (!canSubmit) return;

    setBusy(true);
    setErr("");
    try {
      await createPosProductSubmission({
        branchId: Number(branchId),
        name,
        unitPrice: Number(unitPrice),
        taxRate: String(taxRate).trim() === "" ? 0 : Number(taxRate),
        initialQuantity: Number(initialQuantity),
      });

      setName("");
      setUnitPrice("");
      setTaxRate("");
      setInitialQuantity("");
      await load();
    } catch (e) {
      setErr("Submit failed. Check fields and try again.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="ucx-section ucx-portal-page">
      <div className="ucx-container">
        <ManufacturerNav />

        <div className="ucx-portal-panel">
          <div className="ucx-portal-head">
            <div>
              <h1 className="ucx-portal-title">POS Inventory — Submit Product</h1>
              <p className="ucx-portal-sub">
                Submissions require System Admin approval before appearing to cashier.
              </p>
            </div>
            <div className="ucx-portal-actions">
              <button className="ucx-btn ucx-btn-outline" type="button" onClick={load} disabled={busy}>
                Refresh
              </button>
            </div>
          </div>

          {err ? <div className="ucx-portal-error">{err}</div> : null}

          <div className="ucx-portal-card-large">
            <div className="ucx-portal-panel-title">New submission</div>

            <form onSubmit={onSubmit}>
              <div className="ucx-portal-form">
                <div className="ucx-portal-field">
                  <label>Branch</label>
                  <select value={branchId} onChange={(e) => setBranchId(e.target.value)} disabled={busy}>
                    <option value="">Select branch…</option>
                    {branches.map((b) => (
                      <option key={b.id} value={b.id}>
                        {b.name} (#{b.id})
                      </option>
                    ))}
                  </select>
                </div>

                <div className="ucx-portal-field">
                  <label>Product name</label>
                  <input
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="e.g. Cooking Oil 2L"
                    disabled={busy}
                  />
                </div>

                <div className="ucx-portal-field">
                  <label>Unit price</label>
                  <input
                    value={unitPrice}
                    onChange={(e) => setUnitPrice(e.target.value)}
                    placeholder="e.g. 25.00"
                    type="number"
                    step="0.01"
                    min="0"
                    disabled={busy}
                  />
                </div>

                <div className="ucx-portal-field">
                  <label>Tax rate (0–1)</label>
                  <input
                    value={taxRate}
                    onChange={(e) => setTaxRate(e.target.value)}
                    placeholder="e.g. 0.15 (15%)"
                    type="number"
                    step="0.0001"
                    min="0"
                    max="1"
                    disabled={busy}
                  />
                  <div className="ucx-muted" style={{ marginTop: 6 }}>
                    Example: 0.15 = 15% VAT. Leave blank for 0.
                  </div>
                </div>

                <div className="ucx-portal-field">
                  <label>Initial quantity</label>
                  <input
                    value={initialQuantity}
                    onChange={(e) => setInitialQuantity(e.target.value)}
                    placeholder="e.g. 10"
                    type="number"
                    step="1"
                    min="1"
                    disabled={busy}
                  />
                </div>
              </div>

              <div className="ucx-portal-toolbar">
                <div className="ucx-portal-toolbar-hint">
                  Only approved products become searchable on the cashier POS page.
                </div>
                <button className="ucx-btn ucx-btn-primary" type="submit" disabled={!canSubmit || busy}>
                  {busy ? "Submitting…" : "Submit for approval"}
                </button>
              </div>
            </form>
          </div>

          <div className="ucx-portal-card-large" style={{ marginTop: 14 }}>
            <div className="ucx-portal-panel-title">My submissions</div>

            {loading ? (
              <div className="ucx-muted">Loading…</div>
            ) : (
              <div className="ucx-portal-tablewrap">
                <table className="ucx-portal-table" style={{ minWidth: 900 }}>
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Branch</th>
                      <th>Name</th>
                      <th>Price</th>
                      <th>Qty</th>
                      <th>Status</th>
                      <th>Review note</th>
                      <th>Created</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.length ? (
                      rows.map((r) => (
                        <tr key={r.id}>
                          <td>{r.id}</td>
                          <td>{r.branch_name || r.branch_id}</td>
                          <td>{r.name}</td>
                          <td>{money(r.unit_price)}</td>
                          <td>{r.initial_quantity}</td>
                          <td>{r.status}</td>
                          <td style={{ whiteSpace: "pre-wrap" }}>{r.review_note || "-"}</td>
                          <td>{r.created_at ? String(r.created_at).slice(0, 19).replace("T", " ") : "-"}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="8" className="ucx-muted">
                          No submissions yet.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
